/**
 * Deterministic friendly display name derived from an EVM address.
 *
 * Combines an adjective and a noun picked from curated water/ocean lists,
 * with the address-hex hash as the seed. ~1k combinations — collisions are
 * possible, but the truncated address rendered alongside the name in the
 * leaderboard and profile gives the disambiguation users need.
 */

const ADJECTIVES = [
  'Calm',
  'Brave',
  'Bright',
  'Wise',
  'Bold',
  'Quick',
  'Quiet',
  'Steady',
  'Swift',
  'Deep',
  'Free',
  'Gentle',
  'Lively',
  'Mighty',
  'Noble',
  'Pure',
  'Royal',
  'Serene',
  'Silent',
  'Sleek',
  'Smooth',
  'Strong',
  'Sunny',
  'Tender',
  'Tidal',
  'Tranquil',
  'Vivid',
  'Warm',
  'Wild',
  'Witty',
  'Zen',
  'Cool',
];

const NOUNS = [
  'Wave',
  'Tide',
  'Reef',
  'Coral',
  'Otter',
  'Whale',
  'Orca',
  'Dolphin',
  'Shark',
  'Manta',
  'Heron',
  'Mako',
  'Nautilus',
  'Pearl',
  'Seal',
  'Squid',
  'Tuna',
  'Turtle',
  'Beluga',
  'Marlin',
  'Mariner',
  'Sailor',
  'Compass',
  'Lagoon',
  'Horizon',
  'Atoll',
  'Anchor',
  'Beacon',
  'Tempest',
  'Cove',
  'Channel',
  'Current',
];

/**
 * FNV-1a 32-bit. The polynomial hash with base 31 collapses repetitive
 * addresses (all-zero, all-f) onto the same bucket when the modulus is a
 * power of two — FNV's prime 16777619 doesn't have that pathology.
 */
function hashSlice(hex: string, modulus: number): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < hex.length; i++) {
    h ^= hex.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h % modulus;
}

/**
 * Returns a friendly name like "Quiet Otter" for any EVM-looking address.
 * Stable across renders and across viewers (everyone sees the same name
 * for the same wallet).
 */
export function walletDisplayName(address: string): string {
  const hex = address.toLowerCase().replace(/^0x/, '');
  if (hex.length === 0) return 'Anonymous Wave';
  // Use disjoint halves for adjective vs noun so similar prefixes don't
  // collapse onto the same pair.
  const half = Math.ceil(hex.length / 2);
  const adjective = ADJECTIVES[hashSlice(hex.slice(0, half), ADJECTIVES.length)];
  const noun = NOUNS[hashSlice(hex.slice(half), NOUNS.length)];
  return `${adjective} ${noun}`;
}
